import { AddressSubject } from "@omni/types/api/customer/cloud/shared/enums/addressSubject.enum";
import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { Country } from "@omni/types/enums/country.enum";
import {
    createContext,
    lazy,
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";

import { clearMessageById } from "@/redux/modules/value/messages/messages.utils";
import BpostService, { ErrorField } from "@/services/bpost.service";
import { BPOSTInvalidAddressMessageId } from "@/utils/address/validation.config";
import {
    AddressToValidate,
    BusinessAddressToValidate
} from "@/utils/address/validation.types";

const InvalidAddressModal = lazy(() =>
    import(
        "@/components/Address/AddressValidation/components/InvalidAddressModal/invalidAddressModal.component"
    ).then((module) => ({ default: module.InvalidAddressModal }))
);
const CreateOrEditAddressModal = lazy(() =>
    import(
        "@/components/Address/modals/CreateOrEditAddressModal/createOrEditAddressModal.component"
    ).then((module) => ({ default: module.CreateOrEditAddressModal }))
);

export type ValidationAddress = {
    id?: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    street: string;
    number: string;
    zipCode: string;
    city: string;
    country: string;
    box?: string | null;
    addressing?: string | null;
    validationStatus: AddressValidationStatus;
};

export type AddressValidationResult = {
    validationStatus: AddressValidationStatus;
    /**
     * Set only when the user saved a corrected address through the edit/create modal.
     * A caller that saves the address itself (`savesAddress`) then gets `Closed`, as there is nothing left to save.
     */
    addressId?: string;
};

export type AddressFormProviderValues = {
    errorFields: Array<ErrorField>;
    isValidating: boolean;
    validate: (
        addressToValidate: AddressToValidate
    ) => Promise<AddressValidationResult>;
    validateBusiness: (
        businessAddressToValidate: BusinessAddressToValidate
    ) => Promise<AddressValidationResult>;
    openAddressModal: (address?: any) => void;
    onAddressCreated?: (id: string) => void;
    onAddressEdited?: (id: string) => void;
};

export const AddressFormContext =
    createContext<AddressFormProviderValues | null>(null);

type UseAddressFormProps = {
    onAddressChange?: (addressId: string) => void;
};

type PendingValidation = {
    resolve: (status: AddressValidationResult) => void;
    /** The caller saves the address itself */
    savesAddress?: boolean;
};

export const useAddressForm = ({ onAddressChange }: UseAddressFormProps) => {
    const promiseRef = useRef<PendingValidation | null>(null);

    const editPromiseRef = useRef<PendingValidation | null>(null);

    const [isValidating, setIsValidating] = useState(false);
    const [errorFields, setErrorFields] = useState<Array<ErrorField>>([]);
    const [isEditAddressModalOpen, setIsEditAddresModalOpen] = useState(false);
    const [invalidAddressModal, setInvalidAddresModal] = useState<
        undefined | AddressSubject
    >(undefined);
    const [address, setAddress] = useState<ValidationAddress | null>();
    const [type, setType] = useState<AddressType | null>(null);

    useEffect(() => {
        if (!isEditAddressModalOpen) {
            clearMessageById(BPOSTInvalidAddressMessageId);
        }
    }, [isEditAddressModalOpen]);

    const clearState = useCallback(() => {
        setIsEditAddresModalOpen(false);
        setInvalidAddresModal(undefined);
        setAddress(null);
        setType(null);
        setIsValidating(false);
    }, []);

    const openAddressModal = useCallback((address?: any) => {
        setAddress(address);
        setIsEditAddresModalOpen(true);
    }, []);

    const validate = useCallback(
        async ({
            id,
            firstName,
            lastName,
            street,
            number,
            zipCode,
            city,
            country,
            box,
            addressing,
            type,
            isBpostField,
            currentStatus,
            savesAddress
        }: AddressToValidate) => {
            return new Promise<AddressValidationResult>((resolve) => {
                setErrorFields([]);

                if (isBpostField) {
                    return resolve({
                        validationStatus: AddressValidationStatus.Validated
                    });
                }

                if (country !== Country.Be) {
                    return resolve({
                        validationStatus:
                            AddressValidationStatus.NoValidationNeeded
                    });
                }

                if (
                    currentStatus === AddressValidationStatus.Validated ||
                    currentStatus ===
                        AddressValidationStatus.OverruledByCustomer ||
                    currentStatus === AddressValidationStatus.NoValidationNeeded
                ) {
                    return resolve({
                        validationStatus: currentStatus
                    });
                }

                setType(type || null);
                setIsValidating(true);

                BpostService.validateAddress({
                    street,
                    number,
                    zipCode,
                    city,
                    box
                }).then((validationResult) => {
                    setIsValidating(false);

                    setErrorFields(validationResult.errorFields || []);

                    if (
                        validationResult.isValid &&
                        validationResult.newStatus
                    ) {
                        return resolve({
                            validationStatus: validationResult.newStatus
                        });
                    }

                    promiseRef.current = { resolve, savesAddress };

                    setAddress({
                        id,
                        firstName,
                        lastName,
                        street,
                        number,
                        zipCode,
                        city,
                        box,
                        country,
                        addressing,
                        validationStatus: AddressValidationStatus.Invalid
                    });

                    setIsEditAddresModalOpen(false);
                    setInvalidAddresModal(AddressSubject.Private);
                });
            });
        },
        []
    );

    const validateBusiness = useCallback(
        async ({
            companyName,
            street,
            number,
            zipCode,
            city,
            country,
            box,
            addressing,
            type,
            isBpostField,
            currentStatus
        }: BusinessAddressToValidate) => {
            return new Promise<AddressValidationResult>((resolve) => {
                setErrorFields([]);

                if (isBpostField) {
                    return resolve({
                        validationStatus: AddressValidationStatus.Validated
                    });
                }

                if (country !== Country.Be) {
                    return resolve({
                        validationStatus:
                            AddressValidationStatus.NoValidationNeeded
                    });
                }

                if (
                    currentStatus === AddressValidationStatus.Validated ||
                    currentStatus ===
                        AddressValidationStatus.OverruledByCustomer ||
                    currentStatus === AddressValidationStatus.NoValidationNeeded
                ) {
                    return resolve({
                        validationStatus: currentStatus
                    });
                }

                setType(type || null);
                setIsValidating(true);

                BpostService.validateAddress({
                    street,
                    number,
                    zipCode,
                    city,
                    box
                }).then((validationResult) => {
                    setIsValidating(false);

                    setErrorFields(validationResult.errorFields || []);

                    if (
                        validationResult.isValid &&
                        validationResult.newStatus
                    ) {
                        return resolve({
                            validationStatus: validationResult.newStatus
                        });
                    }

                    promiseRef.current = { resolve };

                    setAddress({
                        companyName,
                        street,
                        number,
                        zipCode,
                        city,
                        box,
                        country,
                        addressing,
                        validationStatus: AddressValidationStatus.Invalid
                    });

                    setInvalidAddresModal(AddressSubject.Business);
                });
            });
        },
        []
    );

    const releaseInnerPromise = useCallback(() => {
        if (
            editPromiseRef.current &&
            promiseRef.current !== editPromiseRef.current
        ) {
            promiseRef.current?.resolve({
                validationStatus: AddressValidationStatus.Closed
            });
        }
    }, []);

    const handleOnInvalidAddressModalEdit = useCallback(() => {
        setInvalidAddresModal(undefined);

        if (invalidAddressModal === "private") {
            editPromiseRef.current ??= promiseRef.current;
            setIsEditAddresModalOpen(true);
        }
    }, [invalidAddressModal]);

    const handleOnInvalidAddressModalClose = useCallback(() => {
        if (isEditAddressModalOpen) {
            setInvalidAddresModal(undefined);
        } else {
            setErrorFields([]);
            clearState();
            editPromiseRef.current?.resolve({
                validationStatus: AddressValidationStatus.Closed
            });
            editPromiseRef.current = null;
        }
        promiseRef.current?.resolve({
            validationStatus: AddressValidationStatus.Closed
        });
    }, [clearState, isEditAddressModalOpen]);

    const handleOnInvalidAddressModalConfirm = useCallback(() => {
        clearState();
        setErrorFields([]);
        promiseRef.current?.resolve({
            validationStatus: AddressValidationStatus.OverruledByCustomer
        });
    }, [clearState]);

    const handleOnCreateOrEditAddressModalChange = useCallback(
        (addressId: string) => {
            onAddressChange?.(addressId);
            clearState();
            setErrorFields([]);
            releaseInnerPromise();

            const pending = editPromiseRef.current ?? promiseRef.current;

            if (!pending) return;

            pending.resolve({
                validationStatus: pending.savesAddress
                    ? AddressValidationStatus.Closed
                    : AddressValidationStatus.Validated,
                addressId
            });

            editPromiseRef.current = null;
        },
        [clearState, onAddressChange, releaseInnerPromise]
    );

    const handleOnCreateOrEditAddressModalOpen = useCallback(() => {
        setErrorFields([]);
        clearState();
        releaseInnerPromise();

        (editPromiseRef.current ?? promiseRef.current)?.resolve({
            validationStatus: AddressValidationStatus.Closed
        });

        editPromiseRef.current = null;
    }, [clearState, releaseInnerPromise]);

    const AddressFormModals = (
        <>
            <InvalidAddressModal
                business={invalidAddressModal === "business"}
                type={type}
                address={address}
                isOpen={!!invalidAddressModal}
                onEdit={handleOnInvalidAddressModalEdit}
                onClose={handleOnInvalidAddressModalClose}
                onConfirm={handleOnInvalidAddressModalConfirm}
            />
            <CreateOrEditAddressModal
                address={address}
                open={isEditAddressModalOpen}
                type={type}
                skipValidation={false}
                onChange={handleOnCreateOrEditAddressModalChange}
                setOpen={handleOnCreateOrEditAddressModalOpen}
            />
        </>
    );

    return {
        errorFields: errorFields,
        isValidating: isValidating,
        validate: validate,
        validateBusiness: validateBusiness,
        openAddressModal: openAddressModal,
        AddressFormModals: AddressFormModals
    };
};
